import { ChangeEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageLayout } from '../../PageLayout';
import { TextBlock } from '../../Atoms/Text';
import { DEFAULT_ADVANCE_BUTTON_STYLE, INPUT_STYLE, RAISED_DIALOG } from '../../../utils/styleHelpers';
import { verifyOtpApi, resendOtpApi } from '../../../Api/User/otpApi';
import { updateSession } from '../../../Api/useSession';
import { safePathName } from '../../../utils/helpers';

type MfaState = { otpSessionToken?: string; redirectTo?: string } | null;

export const MfaPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { otpSessionToken = '', redirectTo = '/' } = (state as MfaState) ?? {};

  const [code, setCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [resendMessage, setResendMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!otpSessionToken) {
      navigate('/login');
    }
  }, [otpSessionToken, navigate]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setErrorMessage('');
    setCode(e.target.value.trim());
  };

  const handleSubmit = async () => {
    if (!code) return;

    // If there is no OTP session token, show a friendly error and redirect to login
    if (!otpSessionToken) {
      setErrorMessage('Your session has expired. Please log in again.');
      navigate('/login');
      return;
    }

    setLoading(true);

    const { data, headers, error } = await verifyOtpApi(otpSessionToken, code);

    if (error) {
      setErrorMessage(error.message);
      setLoading(false);
      return;
    }

    if (data && headers) {
      const authorization = (headers as Record<string, string>)['authorization'] || '';
      const token = authorization.replace('Bearer ', '');

      localStorage.setItem('token', token);

      updateSession({ authenticated: true, token });
      navigate(safePathName(redirectTo));
    }

    setLoading(false);
  };

  const handleResend = async () => {
    setResendMessage('');
    const { error } = await resendOtpApi(otpSessionToken);
    if (error) {
      setErrorMessage(error.message);
    } else {
      setResendMessage('A new code has been sent to your email.');
    }
  };

  return (
    <PageLayout>
      <div className="flex justify-center h-full">
        <div className="grid place-items-center content-center">
          <div className={RAISED_DIALOG}>
            <div
              className="p-1"
              data-cy="mfa-modal">
              <div className="w-full">
                <img
                  alt="UPSTACK Logo"
                  className="m-auto w-36"
                  src="/UPSTACK_logo_with_text.svg"
                />
              </div>
              <div className="my-6 border-t"></div>
              <TextBlock
                className="text-center font-medium text-grey-10"
                size="md"
                weight="bold">
                Two-Factor Authentication
              </TextBlock>
              <TextBlock
                className="text-center text-grey-7 mt-2 mb-6"
                size="sm">
                Enter the verification code sent to your email.
              </TextBlock>
              <form className="bg-white px-1 pt-2 mb-4">
                <label
                  className="text-grey-7 text-sm mb-5"
                  htmlFor="otp-code">
                  Verification Code
                </label>
                <input
                  className={`mb-4 mt-2 ${INPUT_STYLE}`}
                  data-cy="otp-code"
                  id="otp-code"
                  maxLength={6}
                  onChange={handleChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                  type="text"
                  value={code}
                />
                <TextBlock
                  className="max-w-xs break-normal"
                  color="red"
                  size="sm">
                  {errorMessage}
                </TextBlock>
                {resendMessage && (
                  <TextBlock
                    className="max-w-xs break-normal"
                    color="green"
                    size="sm">
                    {resendMessage}
                  </TextBlock>
                )}
                <div className="mt-8">
                  <button
                    className={`${DEFAULT_ADVANCE_BUTTON_STYLE} w-full py-3`}
                    data-cy="verify-otp"
                    disabled={loading || !code}
                    onClick={handleSubmit}
                    type="button">
                    Verify
                  </button>
                </div>
              </form>
              <p className="text-center text-grey-7 text-sm mt-5">
                Code is only valid for 10 minutes <br />
                <button
                  className="text-indigo cursor-pointer"
                  onClick={handleResend}
                  type="button">
                  Resend Code
                </button>{' '}
              </p>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
